import React, { useMemo } from 'react';
import _ from 'lodash';

import { useAITTheme } from '@this/src/components/shared/ui/theme';
import type TransportElement from '@this/domain/transport_element';
import type OrderItem from '@this/domain/order_item';
import TravelerShinkansenType from '@this/src/domain/traveler/traveler_shinkansen_type';
import type ElementBase from '@this/src/domain/element_base';
import type ElementBaseInterface from '@this/src/domain/element_base_interface';
import type { ValueType } from './railway_seat_type_form';
import { convertValueType } from './railway_seat_type_form';

interface Props {
  element: TransportElement;
  orderItem: OrderItem;
  transportIndex: number;
  classNameForModifiedField: (
    pathName: 'shinkansenSeatType' | 'shinkansenTicketType',
    transportIndex: number,
    travelerIndex?: number
  ) => string;
}

export const RailwayTicketTypeForm: React.FC<Props> = ({
  element,
  orderItem,
  transportIndex,
  classNameForModifiedField
}) => {
  const { theme } = useAITTheme();

  const valueType = useMemo((): ValueType => {
    return convertValueType(element.railwaySeatType, orderItem.elements.length);
  }, [element.railwaySeatType, orderItem.elements.length]);

  if (valueType === 'singleTraveler') {
    return (
      <select
        value={element.railwayTicketType || 0}
        onChange={e => element.handleRailwayTicketTypeChange(e.target.value)}
      >
        <option value={0}>通常きっぷ</option>
        <option value={1}>JR九州きっぷ</option>
        <option value={2}>回数券</option>
        <option value={3}>EX-IC</option>
      </select>
    );
  }

  return (
    <>
      {orderItem.travelerInformations.map((ti: any, travelerIndex: number) => (
        <div
          key={travelerIndex}
          className={classNameForModifiedField('shinkansenTicketType', transportIndex, travelerIndex)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: theme.tokens.colors.text.placeholder
          }}
        >
          <select
            value={travelerShinkansenTicketType(valueType, orderItem, transportIndex, travelerIndex) || 0}
            onChange={e =>
              setShinkansenTicketTypeFromNumber(
                valueType,
                orderItem,
                transportIndex,
                travelerIndex,
                parseInt(e.target.value, 10)
              )
            }
          >
            <option value={0}>通常きっぷ</option>
            <option value={1}>JR九州きっぷ</option>
            <option value={2}>回数券</option>
            <option value={3}>EX-IC</option>
          </select>
          {(travelerIndex > 0 || orderItem.travelerInformations.length > 1) && (
            <span>
              ({ti.lastNameKana ? `${ti.lastNameKana} ${ti.firstNameKana}` : ti.email ? ti.email : ti.user.email})
            </span>
          )}
        </div>
      ))}
    </>
  );
};

const travelerShinkansenTicketType = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number
) => {
  if (orderItem.travelerInformations.length === 0) {
    return null;
  }

  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];
  const ticketType =
    valueType === 'multipleTravelersWithSeatType'
      ? traveler.travelerInformationShinkansenTypes[transportIndex]?.shinkansenTicketType ??
        traveler.shinkansenTicketType
      : traveler.shinkansenTicketType;

  switch (ticketType) {
    case 'kyuusyuu':
      return 1;
    case 'bulk_ticket':
      return 2;
    case 'ex':
      return 3;
    default:
      return 0;
  }
};

const setShinkansenTicketTypeFromNumber = (
  valueType: ValueType,
  orderItem: OrderItem,
  transportIndex: number,
  travelerIndex: number,
  value: number
) => {
  if (valueType === 'multipleTravelersWithSeatType') {
    initializeTravelerShinkansenTypes(orderItem);
  }

  const shinkansenTicketTypes: Record<number, string> = {
    0: 'normal',
    1: 'kyuusyuu',
    2: 'bulk_ticket',
    3: 'ex'
  };
  const shinkansenTicketType = shinkansenTicketTypes[value] || 'normal';
  const traveler = _.sortBy(orderItem.travelerInformations, ti => ti.id)[travelerIndex];

  if (valueType === 'multipleTravelersWithSeatType') {
    const shinkansenType = traveler.travelerInformationShinkansenTypes[transportIndex];

    if (shinkansenType) {
      shinkansenType.shinkansenTicketType = shinkansenTicketType;
    }
  } else {
    traveler.shinkansenTicketType = shinkansenTicketType;
  }

  app.render();
};

export const initializeTravelerShinkansenTypes = (orderItem: OrderItem) => {
  orderItem.travelerInformations.forEach((ti: any) => {
    if (_.isEmpty(ti.travelerInformationShinkansenTypes)) {
      ti.travelerInformationShinkansenTypes = [];
    }

    orderItem.elements.forEach((element: ElementBase | ElementBaseInterface, elementIndex: number) => {
      if (!ti.travelerInformationShinkansenTypes[elementIndex]) {
        const el = element as TransportElement;
        const shinkansenType = new TravelerShinkansenType({
          travelerInformationId: ti.id,
          shinkansenTicketType: ti.shinkansenTicketType || el.railwayTicketType || 'normal',
          shinkansenSeatType: ti.shinkansenSeatType || el.railwaySeatType || 'normal'
        });
        ti.travelerInformationShinkansenTypes[elementIndex] = shinkansenType;
      }
    });
  });
};
